import { useEffect, useState, useMemo, useCallback } from "react";

import { getElectronicDocumentsSettings, addResolution, updateResolution, toggleEnableSettings } from '../../../usecases/documents-usecase'

import Table from "../../molecules/tablas/view";
import InvoiceSettingsForm from "../../molecules/InvoiceSettingsForm/view";
import ModalBtn from "../../molecules/modalButton/view";
import FacetedFilter from "../../molecules/FacetedFilter/view";
import { dispatchCustomEvent } from "../../../utils/dispatch";
import CustomBox from "../../atoms/container/container";
import { Switch } from '@mui/material';
import { FileText, ToggleLeft, Layers } from 'lucide-react';

function ElectronicDocsSettings({ profile }) {

    const [sellSettings, setSellSettings] = useState({
        data: undefined,
        page: 1,
        page_size: 50,
        total: 0
    });
    const [rawSettings, setRawSettings] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [activeFilters, setActiveFilters] = useState([]);

    const header = ["Resolucion", "Activa", "Ambito", "Documento", "Tipo resolucion", "Prefijo", "Numeracion", "Vigente", "Ultimo usado"]
    const DocumentTypes = Object.freeze({
        1: "Factura",
        4: "Nota credito",
        5: "Nota debito",
        11: "Documento soporte",
        13: "Nota de ajuste documento soporte"
    });
    const TypesResolution = Object.freeze({
        1: "Electronica",
        2: "Papel",
        3: "Talonario"
    });

    const AppliesToBadge = {
        pos: { label: "POS", color: "#e0f2fe", text: "#0369a1", border: "#bae6fd" },
        services: { label: "Servicios", color: "#fef3c7", text: "#b45309", border: "#fde68a" },
        both: { label: "Ambos", color: "#dcfce7", text: "#15803d", border: "#bbf7d0" },
    };

    const filtersConfig = useMemo(() => [
        {
            key: 'type_document',
            label: 'Tipo Documento',
            icon: <FileText size={14} />,
            type: 'select',
            options: [
                { value: 'Factura', label: 'Factura' },
                { value: 'Nota credito', label: 'Nota Crédito' },
                { value: 'Nota debito', label: 'Nota Débito' },
                { value: 'Documento soporte', label: 'Documento Soporte' },
                { value: 'Nota de ajuste documento soporte', label: 'Nota de Ajuste DS' },
            ]
        },
        {
            key: 'applies_to',
            label: 'Ámbito',
            icon: <Layers size={14} />,
            type: 'select',
            options: [
                { value: 'both', label: 'Ambos (POS y Servicios)' },
                { value: 'pos', label: 'Punto de Venta (POS)' },
                { value: 'services', label: 'Servicios' },
            ]
        },
        {
            key: 'enable',
            label: 'Estado',
            icon: <ToggleLeft size={14} />,
            type: 'select',
            options: [
                { value: 'true', label: 'Activas' },
                { value: 'false', label: 'Inactivas' },
            ]
        }
    ], []);

    const applyFilters = useCallback((mappedList, filters) => {
        let result = [...mappedList];

        filters.forEach(f => {
            if (f.key === 'type_document') {
                result = result.filter(item => item.type_document === f.value);
            }
            if (f.key === 'applies_to') {
                result = result.filter(item => (item._rawAppliesTo || 'both') === f.value);
            }
            if (f.key === 'enable') {
                const isEnable = f.value === 'true';
                result = result.filter(item => Boolean(item._rawEnable) === isEnable);
            }
        });

        return result;
    }, []);

    const fetchResolutions = () => {
        setIsLoading(true);
        getElectronicDocumentsSettings(profile.uid, profile.cid).then((response) => {
            if (!response || response[0]?.message === 'empty_array') {
                setRawSettings([]);
                setSellSettings({
                    data: [],
                    page: 1,
                    page_size: 50,
                    total: 0
                });
            } else {
                const mapped = response.map((itm) => (table_mapper(itm)));
                setRawSettings(mapped);
                const filtered = applyFilters(mapped, activeFilters);
                setSellSettings({
                    data: filtered,
                    page: 1,
                    page_size: 50,
                    total: filtered.length
                });
            }
        }).catch((err) => {
            console.error("Error obteniendo la informacion de la factura electronica: ", err);
            setRawSettings([]);
            setSellSettings({
                data: [],
                page: 1,
                page_size: 50,
                total: 0
            });
        }).finally(() => {
            setIsLoading(false);
        });
    };

    useEffect(() => {
        fetchResolutions();
    }, []);

    useEffect(() => {
        if (rawSettings.length === 0) return;
        const filtered = applyFilters(rawSettings, activeFilters);
        setSellSettings(prev => ({
            ...prev,
            data: filtered,
            total: filtered.length
        }));
    }, [activeFilters, applyFilters, rawSettings]);

    function table_mapper(itm) {
        const rawAppliesTo = itm.applies_to || 'both';
        const badgeStyle = AppliesToBadge[rawAppliesTo] || AppliesToBadge.both;

        return {
            id: itm.id,
            resolution: itm.resolution,
            enable: <Switch defaultChecked={itm.enable} onChange={(e) => handleEnable(itm.id, e.target.checked, itm)}></Switch>,
            ambito: (
                <select
                    value={rawAppliesTo}
                    onChange={(e) => handleUpdateAppliesTo(itm.id, e.target.value, itm)}
                    style={{
                        padding: "3px 8px",
                        borderRadius: "12px",
                        fontSize: "11px",
                        fontWeight: "600",
                        backgroundColor: badgeStyle.color,
                        color: badgeStyle.text,
                        border: `1px solid ${badgeStyle.border}`,
                        cursor: "pointer",
                        outline: "none",
                    }}
                    title="Cambiar ámbito de aplicación"
                >
                    <option value="both">Ambos</option>
                    <option value="pos">POS</option>
                    <option value="services">Servicios</option>
                </select>
            ),
            type_document: DocumentTypes[itm.type_document_id],
            type_resolution: TypesResolution[itm.type_resolution],
            prefix: itm.prefix,
            numeration: `${itm.from_number} - ${itm.to_number}`,
            date_to: itm.date_to,
            last_number_used: itm.lastNumberUsed,
            _rawId: itm.id,
            _rawEnable: itm.enable,
            _rawAppliesTo: rawAppliesTo,
            _rawDocTypeId: itm.type_document_id
        }
    }

    function handleUpdateAppliesTo(id, newAppliesTo, itm) {
        if (itm.enable) {
            const conflict = rawSettings.find(s => 
                (s._rawId || s.id) !== id &&
                s._rawEnable &&
                s._rawDocTypeId === itm.type_document_id &&
                (newAppliesTo === 'both' || s._rawAppliesTo === 'both' || s._rawAppliesTo === newAppliesTo)
            );
            if (conflict) {
                dispatchCustomEvent('popup', { 
                    message: `Conflicto: Ya existe otra resolución activa para este documento y ámbito (${conflict._rawAppliesTo || 'Ambos'}). Desactívala antes de cambiar el ámbito.`, 
                    type: "warning" 
                });
                fetchResolutions();
                return;
            }
        }

        updateResolution("", "", id, { applies_to: newAppliesTo })
            .then(() => {
                dispatchCustomEvent('popup', { message: "Ámbito actualizado con éxito", type: "success" });
                fetchResolutions();
            })
            .catch((err) => {
                const errorMsg = err?.response?.data?.message || err?.message || "Error actualizando ámbito";
                dispatchCustomEvent('popup', { message: errorMsg, type: "error" });
                fetchResolutions();
            });
    }

    function handleEnable(id, enable, itm) {
        // Validación preventiva en cliente para conflicto de ámbito activo
        if (enable) {
            const rawAppliesTo = itm.applies_to || 'both';
            const conflict = rawSettings.find(s => 
                (s._rawId || s.id) !== id &&
                s._rawEnable &&
                s._rawDocTypeId === itm.type_document_id &&
                (rawAppliesTo === 'both' || s._rawAppliesTo === 'both' || s._rawAppliesTo === rawAppliesTo)
            );
            if (conflict) {
                dispatchCustomEvent('popup', { 
                    message: `Ya existe una resolución activa para este tipo de documento y ámbito (${conflict._rawAppliesTo || 'Ambos'}). Desactívala primero.`, 
                    type: "warning" 
                });
                fetchResolutions();
                return;
            }
        }

        toggleEnableSettings("", "", id, enable).then(() => {
            dispatchCustomEvent('popup', { message: "Actualizado con exito", type: "success" });
            fetchResolutions();
        }).catch((err) => {
            const errorMsg = err?.response?.data?.message || err?.message || "Error actualizando";
            dispatchCustomEvent('popup', { message: errorMsg, type: "error" });
            dispatchCustomEvent('loading-btn', {});
            fetchResolutions();
        })
    }

    function handleNewResol(data) {
        addResolution("", "", data).then((res) => {
            fetchResolutions();
            dispatchCustomEvent('popup', { message: "Creada con exito", type: "success" });
            dispatchCustomEvent('modal-close')
        }).catch((err) => {
            const errorMsg = err?.response?.data?.message || err?.message || "Error creando resolución";
            dispatchCustomEvent('popup', { message: errorMsg, type: "error" });
            dispatchCustomEvent('loading-btn', {})
        })
    }

    return (
        <CustomBox style={{ maxHeight: '90%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <ModalBtn id="add-resolution-modal" type="pill-primary" title="Agregar resolucion" >
                    <InvoiceSettingsForm onSubmit={handleNewResol}></InvoiceSettingsForm>
                </ModalBtn>
                <FacetedFilter
                    filters={filtersConfig}
                    activeFilters={activeFilters}
                    onChange={setActiveFilters}
                    onClear={() => setActiveFilters([])}
                />
            </div>
            <Table 
                id="resolutions-table"
                paginatedData={sellSettings}
                headersList={header}
                excludeList={["_rawEnable", "_rawAppliesTo", "_rawDocTypeId"]}
                isLoading={isLoading}
                allowPageSizeControl={true}
                getFunction={fetchResolutions}
            />
        </CustomBox>
    )
}

export default ElectronicDocsSettings
